import React from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import Shell from "../components/Shell";
import Deck from "../components/Deck";
import FilterSheet from "../components/FilterSheet";
import { WinnersBanner } from "../components/winners/WinnersBanner";
import SupportSheet from "../components/SupportSheet";
import { SUPPORT_ENABLED } from "../lib/features";
import { endpoints } from "../lib/api";
import * as store from "../lib/storage";

export default function DeckPage() {
  const [sp, setSp] = useSearchParams();
  const [tags, setTags] = React.useState([]);
  const [projects, setProjects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [support, setSupport] = React.useState(null);
  const [seenVersion, setSeenVersion] = React.useState(0);

  const filters = React.useMemo(() => ({
    track: sp.get("track") || "all",
    tags: sp.getAll("tag"),
    q: sp.get("q") || "",
  }), [sp]);

  const setFilters = (f) => {
    const next = new URLSearchParams();
    if (f.track && f.track !== "all") next.set("track", f.track);
    (f.tags || []).forEach((t) => next.append("tag", t));
    if (f.q) next.set("q", f.q);
    setSp(next);
  };

  React.useEffect(() => { endpoints.tags().then(setTags).catch(() => {}); }, []);

  React.useEffect(() => {
    setLoading(true);
    const params = { session_id: store.getSessionShuffle() };
    if (filters.track !== "all") params.track = filters.track;
    if (filters.tags.length) params.tags = filters.tags.join(",");
    if (filters.q) params.q = filters.q;
    endpoints.projects(params).then((d) => { setProjects(d.projects); setLoading(false); }).catch(() => setLoading(false));
  }, [filters]);

  const seen = store.getSeen();
  const deckProjects = projects.filter((p) => !seen.includes(p.id));
  const hasFilters = filters.track !== "all" || filters.tags.length > 0 || !!filters.q;
  const activeCount = (filters.track !== "all" ? 1 : 0) + filters.tags.length + (filters.q ? 1 : 0);

  const reshuffle = () => {
    store.clearSeen();
    store.reshuffle();
    const params = { session_id: store.getSessionShuffle() };
    if (filters.track !== "all") params.track = filters.track;
    if (filters.tags.length) params.tags = filters.tags.join(",");
    if (filters.q) params.q = filters.q;
    setLoading(true);
    endpoints.projects(params).then((d) => { setProjects(d.projects); setLoading(false); setSeenVersion((v) => v + 1); });
  };

  return (
    <Shell footer={false}>
      <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col px-4 py-4">
        <WinnersBanner className="mb-3" />
        <div className="mb-3 flex items-center justify-between">
          <button onClick={() => setFilterOpen(true)} data-testid="open-filters"
            className="flex items-center gap-2 rounded-pill border border-white/15 bg-white/5 px-4 py-2 text-[14px] font-semibold text-white">
            <SlidersHorizontal size={16} /> Filters {activeCount ? <span className="rounded-full bg-gold-500 px-1.5 text-[11px] text-navy-900">{activeCount}</span> : null}
          </button>
        </div>

        {loading ? (
          <div className="flex flex-1 items-center justify-center text-on-dark-muted" data-testid="deck-loading">Warming up the deck…</div>
        ) : (
          <div className="flex flex-1 items-start justify-center pt-2" key={seenVersion}>
            <Deck
              projects={deckProjects}
              total={projects.length}
              onOpenSupport={(p) => setSupport(p)}
              hasFilters={hasFilters}
              onClearFilters={() => setFilters({ track: "all", tags: [], q: "" })}
              onReshuffle={reshuffle}
            />
          </div>
        )}
      </div>

      <FilterSheet open={filterOpen} onClose={() => setFilterOpen(false)} tags={tags} filters={filters} setFilters={setFilters} resultCount={projects.length} />
      {SUPPORT_ENABLED && <SupportSheet project={support} open={!!support} onClose={() => setSupport(null)} />}
    </Shell>
  );
}
